import React, { useState, useEffect } from "react";
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  IconButton,
  Chip,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";
import { OttoMarginRulesApi } from "../../../../../services/api/multi/otto";

const MarginRulesSubTab = ({ tabValue, index }) => {
  const [marginRules, setMarginRules] = useState([]);
  const [marginRuleDialog, setMarginRuleDialog] = useState(false);
  const [editingMarginRule, setEditingMarginRule] = useState(null);
  const [marginRuleForm, setMarginRuleForm] = useState({
    buy_price: "",
    min_margin: "",
    max_margin: "",
    min_min_profit: "",
    max_min_profit: "",
  });

  useEffect(() => {
    fetchMarginRules();
  }, []);

  const fetchMarginRules = async (forceRefresh = false) => {
    try {
      const CACHE_KEY = "margin_rules_cache";
      const CACHE_DURATION = 300 * 1000; // 300 seconds

      if (!forceRefresh) {
        const cached = localStorage.getItem(CACHE_KEY);
        if (cached) {
          try {
            const { data, timestamp } = JSON.parse(cached);
            if (Date.now() - timestamp < CACHE_DURATION) {
              setMarginRules(data);
              return;
            }
          } catch (e) {
            console.error("Error parsing cache", e);
            localStorage.removeItem(CACHE_KEY);
          }
        }
      }

      const response = await OttoMarginRulesApi.getMarginRules();
      setMarginRules(response.data);

      localStorage.setItem(
        CACHE_KEY,
        JSON.stringify({
          data: response.data,
          timestamp: Date.now(),
        }),
      );
    } catch (error) {
      console.error("Error fetching margin rules:", error);
    }
  };

  const sortedMarginRules = [...marginRules].sort(
    (a, b) => a.buy_price - b.buy_price,
  );
  const lowestRule = sortedMarginRules[0];
  const highestRule = sortedMarginRules[sortedMarginRules.length - 1];

  const handleSaveMarginRule = async () => {
    const rule = {
      buy_price: parseFloat(marginRuleForm.buy_price),
      min_margin: parseFloat(marginRuleForm.min_margin),
      max_margin: parseFloat(marginRuleForm.max_margin),
      min_min_profit: parseFloat(marginRuleForm.min_min_profit),
      max_min_profit: parseFloat(marginRuleForm.max_min_profit),
    };
    try {
      if (editingMarginRule) {
        await OttoMarginRulesApi.updateMarginRule(editingMarginRule.id, rule);
      } else {
        await OttoMarginRulesApi.createMarginRule(rule);
      }
      fetchMarginRules(true);
      setMarginRuleDialog(false);
      setEditingMarginRule(null);
      setMarginRuleForm({
        buy_price: "",
        min_margin: "",
        max_margin: "",
        min_min_profit: "",
        max_min_profit: "",
      });
    } catch (error) {
      console.error("Error saving margin rule:", error);
    }
  };

  const handleEditMarginRule = (rule) => {
    setEditingMarginRule(rule);
    setMarginRuleForm({
      buy_price: rule.buy_price.toString(),
      min_margin: rule.min_margin.toString(),
      max_margin: rule.max_margin.toString(),
      min_min_profit: rule.min_min_profit.toString(),
      max_min_profit: rule.max_min_profit.toString(),
    });
    setMarginRuleDialog(true);
  };

  const handleDeleteMarginRule = async (id) => {
    try {
      await OttoMarginRulesApi.deleteMarginRule(id);
      fetchMarginRules(true);
    } catch (error) {
      console.error("Error deleting margin rule:", error);
    }
  };

  const isFormValid = () => {
    return (
      marginRuleForm.buy_price &&
      marginRuleForm.min_margin &&
      marginRuleForm.max_margin &&
      marginRuleForm.min_min_profit &&
      marginRuleForm.max_min_profit &&
      !isNaN(Number(marginRuleForm.buy_price)) &&
      !isNaN(Number(marginRuleForm.min_margin)) &&
      !isNaN(Number(marginRuleForm.max_margin)) &&
      !isNaN(Number(marginRuleForm.min_min_profit)) &&
      !isNaN(Number(marginRuleForm.max_min_profit))
    );
  };

  return (
    <div
      role="tabpanel"
      hidden={tabValue !== index}
      id={`margin-rules-tabpanel-${index}`}
      aria-labelledby={`margin-rules-tab-${index}`}
    >
      {tabValue === index && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={10}>
            <Card
              sx={{
                border: "3px solid",
                borderColor: "divider",
                borderRadius: 2,
              }}
            >
              <CardContent>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  mb={3}
                >
                  <Typography variant="h6">Margin Rules</Typography>
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<AddIcon />}
                    onClick={() => setMarginRuleDialog(true)}
                  >
                    Add Rule
                  </Button>
                </Box>
                <Alert severity="info" sx={{ mb: 3 }}>
                  <Typography variant="body2">
                    Define your desired min and max margins (%) for specific buy
                    prices (EK). Rules will be interpolated between buy prices.
                    The lowest/highest margin applies below/above the defined
                    range.
                  </Typography>
                </Alert>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>
                          <strong>EK (Buy Price)</strong>
                        </TableCell>
                        <TableCell>
                          <strong>Min Margin (%)</strong>
                        </TableCell>
                        <TableCell>
                          <strong>Max Margin (%)</strong>
                        </TableCell>
                        <TableCell>
                          <strong>Min Profit for Min Margin (€)</strong>
                        </TableCell>
                        <TableCell>
                          <strong>Min Profit for Max Margin (€)</strong>
                        </TableCell>
                        <TableCell align="right">
                          <strong>Actions</strong>
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {/* Always show first row: margin for prices above the last rule */}
                      {sortedMarginRules.length > 0 && (
                        <TableRow>
                          <TableCell>
                            <Typography variant="body1" color="text.secondary">
                              &gt; {Number(highestRule.buy_price).toFixed(2)} €
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body1" color="text.secondary">
                              {Number(highestRule.min_margin).toFixed(2)} %
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body1" color="text.secondary">
                              {Number(highestRule.max_margin).toFixed(2)} %
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body1" color="text.secondary">
                              {Number(highestRule.min_min_profit).toFixed(2)} €
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body1" color="text.secondary">
                              {Number(highestRule.max_min_profit).toFixed(2)} €
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Chip label="Default" size="small" color="info" />
                          </TableCell>
                        </TableRow>
                      )}
                      {marginRules.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={6} align="center">
                            <Typography variant="body2" color="text.secondary">
                              No margin rules configured. Click 'Add Rule' to
                              create your first margin rule.
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        // Reverse the order and add default rows between rules
                        [...marginRules]
                          .sort((a, b) => b.buy_price - a.buy_price)
                          .map((rule, idx, arr) => (
                            <React.Fragment key={rule.id}>
                              <TableRow hover>
                                <TableCell>
                                  <Typography variant="body1">
                                    {Number(rule.buy_price).toFixed(2)} €
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Typography variant="body1">
                                    {Number(rule.min_margin).toFixed(2)} %
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Typography variant="body1">
                                    {Number(rule.max_margin).toFixed(2)} %
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Typography variant="body1">
                                    {Number(rule.min_min_profit).toFixed(2)} €
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Typography variant="body1">
                                    {Number(rule.max_min_profit).toFixed(2)} €
                                  </Typography>
                                </TableCell>
                                <TableCell align="right">
                                  <Box
                                    sx={{
                                      display: "flex",
                                      gap: 1,
                                      justifyContent: "flex-end",
                                    }}
                                  >
                                    <IconButton
                                      color="primary"
                                      onClick={() => handleEditMarginRule(rule)}
                                      title="Edit Rule"
                                    >
                                      <EditIcon />
                                    </IconButton>
                                    <IconButton
                                      color="error"
                                      onClick={() =>
                                        handleDeleteMarginRule(rule.id)
                                      }
                                      title="Delete Rule"
                                    >
                                      <DeleteIcon />
                                    </IconButton>
                                  </Box>
                                </TableCell>
                              </TableRow>
                              {/* Insert default row between two rules */}
                              {idx < arr.length - 1 && (
                                <TableRow>
                                  <TableCell>
                                    <Typography
                                      variant="body1"
                                      color="text.secondary"
                                    >
                                      Between{" "}
                                      {Number(arr[idx + 1].buy_price).toFixed(
                                        2,
                                      )}{" "}
                                      € and {Number(rule.buy_price).toFixed(2)}{" "}
                                      €
                                    </Typography>
                                  </TableCell>
                                  <TableCell>
                                    <Typography
                                      variant="body1"
                                      color="text.secondary"
                                    >
                                      {Number(rule.min_margin).toFixed(2)}%
                                    </Typography>
                                  </TableCell>
                                  <TableCell>
                                    <Typography
                                      variant="body1"
                                      color="text.secondary"
                                    >
                                      {Number(rule.max_margin).toFixed(2)}%
                                    </Typography>
                                  </TableCell>
                                  <TableCell>
                                    <Typography
                                      variant="body1"
                                      color="text.secondary"
                                    >
                                      {Number(rule.min_min_profit).toFixed(2)} €
                                    </Typography>
                                  </TableCell>
                                  <TableCell>
                                    <Typography
                                      variant="body1"
                                      color="text.secondary"
                                    >
                                      {Number(rule.max_min_profit).toFixed(2)} €
                                    </Typography>
                                  </TableCell>
                                  <TableCell align="right">
                                    <Chip
                                      label="Default"
                                      size="small"
                                      color="info"
                                    />
                                  </TableCell>
                                </TableRow>
                              )}
                            </React.Fragment>
                          ))
                      )}
                      {/* Always show last row: margin for prices below the first rule */}
                      {sortedMarginRules.length > 0 && (
                        <TableRow>
                          <TableCell>
                            <Typography variant="body1" color="text.secondary">
                              &lt; {Number(lowestRule.buy_price).toFixed(2)} €
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body1" color="text.secondary">
                              {Number(lowestRule.min_margin).toFixed(2)} %
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body1" color="text.secondary">
                              {Number(lowestRule.max_margin).toFixed(2)} %
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body1" color="text.secondary">
                              {Number(lowestRule.min_min_profit).toFixed(2)} €
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body1" color="text.secondary">
                              {Number(lowestRule.max_min_profit).toFixed(2)} €
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Chip label="Default" size="small" color="info" />
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}
      {/* Create/Edit Margin Rule Form */}
      <Dialog
        open={marginRuleDialog}
        onClose={() => {
          setMarginRuleDialog(false);
          setEditingMarginRule(null);
          setMarginRuleForm({
            buy_price: "",
            min_margin: "",
            max_margin: "",
            min_min_profit: "",
            max_min_profit: "",
          });
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {editingMarginRule ? "Edit Margin Rule" : "Create Margin Rule"}
        </DialogTitle>
        <DialogContent>
          <Box
            component="form"
            sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}
            onSubmit={(e) => {
              e.preventDefault();
              handleSaveMarginRule();
            }}
          >
            <TextField
              fullWidth
              label="Buy Price (EK) *"
              type="number"
              value={marginRuleForm.buy_price}
              onChange={(e) =>
                setMarginRuleForm((prev) => ({
                  ...prev,
                  buy_price: e.target.value,
                }))
              }
              placeholder="e.g., 10.00"
              required
              inputProps={{ min: 0, step: "0.01" }}
            />
            <TextField
              fullWidth
              label="Min Margin (%) *"
              type="number"
              value={marginRuleForm.min_margin}
              onChange={(e) =>
                setMarginRuleForm((prev) => ({
                  ...prev,
                  min_margin: e.target.value,
                }))
              }
              placeholder="e.g., 10.50"
              required
              inputProps={{ min: 0, step: "0.01" }}
            />
            <TextField
              fullWidth
              label="Max Margin (%) *"
              type="number"
              value={marginRuleForm.max_margin}
              onChange={(e) =>
                setMarginRuleForm((prev) => ({
                  ...prev,
                  max_margin: e.target.value,
                }))
              }
              placeholder="e.g., 25.00"
              required
              inputProps={{ min: 0, step: "0.01" }}
            />
            <TextField
              fullWidth
              label="Min Profit for Min Margin (€) *"
              type="number"
              value={marginRuleForm.min_min_profit}
              onChange={(e) =>
                setMarginRuleForm((prev) => ({
                  ...prev,
                  min_min_profit: e.target.value,
                }))
              }
              placeholder="e.g., 2.5"
              required
              inputProps={{ min: 0, step: "0.01" }}
            />
            <TextField
              fullWidth
              label="Min Profit for Max Margin (€) *"
              type="number"
              value={marginRuleForm.max_min_profit}
              onChange={(e) =>
                setMarginRuleForm((prev) => ({
                  ...prev,
                  max_min_profit: e.target.value,
                }))
              }
              placeholder="e.g., 5.0"
              required
              inputProps={{ min: 0, step: "0.01" }}
            />
            <Box sx={{ display: "flex", gap: 2, pt: 2 }}>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={!isFormValid()}
              >
                {editingMarginRule ? "Update" : "Create"}
              </Button>
              <Button
                variant="outlined"
                onClick={() => {
                  setMarginRuleDialog(false);
                  setEditingMarginRule(null);
                  setMarginRuleForm({
                    buy_price: "",
                    min_margin: "",
                    max_margin: "",
                    min_min_profit: "",
                    max_min_profit: "",
                  });
                }}
              >
                Cancel
              </Button>
            </Box>
          </Box>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MarginRulesSubTab;
